<h1>Historical-Critical Method (HCM)</h1>
<ul>
	<li>Used to place writings in their historical, cultural, and social contexts.</li>
	<li><b>Redaction and composition criticism</b>: Covers removals, edits, additions, comments and clarifications.</li>
	<li><b>Textual Criticism</b>: Comparing manuscript variations to reconstruct the original form of the text.</li>
	<li><b>Source Criticism</b>: Identifies the oral and written sources behind the final text, focusing on which sources were combined.</li>
	<li><b>Form Criticism</b>: Categorizes texts by genre to understand their typical structure and original social context.</li>
	<li><b>Tradition Criticism</b>: Traces the oral traditions and narratives over time before they were written down.</li>
	<li><b>Historical and Contextual</b>: When available it uses archeology, sociology, and comparative studies to understand the cultural and historical setting of the author and audience.</li>
	<li><b>Criterion of Embarrassment</b>: The least useful form of criterion and a very poor line of reasoning stating that if a source includes embarrassing, difficult, or counter-productive details about their own group, they believe it increases the probability that the incident likely occurred.
		<ul>
			<li>Gospel embarrassment:
				<ul>
					<li>Jesus is seen as being humbled or submitting to an inferior through baptism.</li>
					<li>Crucifixion is a painful and public form of execution.</li>
					<li>Women as witnesses.</li>
					<li>Disbelief by <a [appLink]="'https://www.biblegateway.com/passage/?search=Mark%203%3A21'">Jesus' family</a>.</li>
				</ul>
			</li>
			<li>Egyptian gods suffer embarrassment:
				<ul>
					<li>Ra's age and secret name. Portraying him as senile, drooling, and manipulatable.</li>
					<li>Ra/Atum created other gods through masturbation.</li>
					<li>Set has his manhood ripped off.</li>
					<li>During the rebellion of humanity Ra and other gods take the form of animals or flee in fear.</li>
					<li>Serapis commanded his temple to be built on a place full of manure.</li>
				</ul>
			</li>
			<li>Greek deities are often humbled and suffer embarrassment:
				<ul>
					<li>Zeus's attempts to avoid prophecies.</li>
					<li>Zeus swallows his pregnant lover Metis.</li>
					<li>Zeus was bound by rebellious gods.</li>
					<li>Zeus was ridiculed for his cowardice in hiding his affairs.</li>
					<li>When Semele died, Zeus took their unborn child and stitched the fetus into his own thigh.</li>
					<li>Zeus also is viewed as lustful for Hera.</li>
					<li>Achilles' vulnerability, a great warrior finished by an unimpressive arrow to the heel.</li>
					<li>Heracles' shame, he killed his family and died in agony from a poisoned shirt.</li>
					<li>Ares trapped in a net by Hephaestus.</li>
					<li>Greek gods often had human flaws, portraying them as relatable reflections of society.</li>
				</ul>
			</li>
			<li>Roman deities also suffered embarrassment:
				<ul>
					<li>Mars and Venus Caught in a Net.</li>
					<li>Ares/Mars Kidnapped and Jarred.</li>
					<li>Arachne is transformed into a spider because of Minerva's rage.</li>
					<li>Diana, embarrassed by her nudity, turns Actaeon into a Stag.</li>
					<li>Hercules was famously mocked and tricked by two mischievous forest spirits, the Cercopes, who stole his weapons while he slept.</li>
					<li>Aidos is a spirit of respect and modesty.</li>
					<li>Vulcan is often involved in moments of shame for other gods.</li>
				</ul>
			</li>
			<p>Depictions of embarrassment were not new by the time the Jesus myth formed. The criterion of embarrassment does not influence the opinion of historians and scholars into believing the other deities are real. It's a reflection of humbling actions and events that were present in society at the time. It was also used in mythology to explain why something exists (spiders etc) or happens.</p>
		</ul>
	</li>
	<li>
		<b>Secular HCM</b>:
		<ul>
			<li>Is archive-driven through administrative records, inscriptions, material culture, and economic data.</li>
			<li>Hypotheses are expected to be minimally sufficient, reconstructions are constrained by external corroboration, models are built on or fail due to evidentiary pressure.</li>
			<li>Encouraged to find novelty inside the same texts, refine compositional models, debate micro-variations in redaction and dating.</li>
			<li>Is more likely to shift questions when sources thin out, abandon unsustainable models, and let uncertainty stand without filling it.</li>
			<li>Identifies if documents are administrative or social and what function they serve.</li>
			<li>The internal politics of secular HCM determine what questions are seen as legitimate, how aggressively uncertainty is embraced, and when a hypothesis is allowed to die.</li>
		</ul>
	</li>
	<li>
		<b>Issues with Biblical HCM</b>:
		<ul>
			<li>Non-textual evidence is sparse, uneven, or late.</li>
			<li>The canon itself defines the collection of text.</li>
			<li>Utilizes lost sources with no external attestation.</li>
			<li>Relies on long chains of editorial intent reconstruction.</li>
			<li>Operates under a fixed, finite, and exhaustively studied collection of text.</li>
			<li>Prioritizes literary-theological compositions first and historical sources second.</li>
			<li>Don't operate under a neutral playing field and are influenced by confessional pressure, public theological implications, and inherited apologetic or counter-apologetic framing.</li>
			<li>Depends on methodological rather than evidentiary debates.</li>
		</ul>
	</li>
</ul>
